/**
 * ============================================================================
 * DESKTOP ICON COMPONENT
 * ============================================================================
 *
 * A draggable desktop icon that opens an associated window when clicked.
 * Supports both React elements and emoji strings as icons.
 *
 * @packageDocumentation
 */

"use client";

import {
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { DesktopIconConfig } from "../types";

let farmImagePreloaded = false;

function preloadFarmAssets(windowId: string) {
  if (
    windowId !== "farm" ||
    farmImagePreloaded ||
    typeof Image === "undefined"
  ) {
    return;
  }

  farmImagePreloaded = true;
  const image = new Image();
  image.src = "/farm.png";
}

/* ==========================================================================
   TYPES
   ========================================================================== */

export interface DesktopIconRef {
  /** Get the current position of the icon (for window animations) */
  getPosition: () => { x: number; y: number };
}

export interface DesktopIconProps {
  /** Icon configuration */
  config: DesktopIconConfig;
  /** Click handler (opens the linked window) */
  onClick: () => void;
  /** Custom class name */
  className?: string;
  /** Whether the icon is displayed in a dock (hides label, disables drag) */
  isDock?: boolean;
  /** Whether the associated window is currently open (shows indicator dot in dock) */
  isOpen?: boolean;
  /** Whether the device is mobile (for responsive sizing) */
  isMobile?: boolean;
  /** Whether to use mobile grid layout (iOS-style with labels) */
  isMobileGrid?: boolean;
  /** Whether the icon is in jiggle mode (long-press reorder) */
  isJiggling?: boolean;
  /** Callback when dock icon is reordered via drag (cursorX = drop position for index resolution) */
  onDockReorder?: (fromIndex: number, cursorX: number) => void;
  /** Index of this icon in the dock (for reorder) */
  dockIndex?: number;
  /** Callback when desktop icon position changes (values in viewport %) */
  onPositionChange?: (
    iconId: string,
    xPercent: number,
    yPercent: number
  ) => void;
  /** Callback for right-click context menu */
  onContextMenu?: (iconId: string, x: number, y: number) => void;
  /** Callback when a dock icon is dragged off the dock onto the desktop */
  onDragOffDock?: (iconId: string, xPercent: number, yPercent: number) => void;
  /** Callback when a desktop icon is dragged onto the dock area */
  onDragToDock?: (iconId: string, cursorX: number) => void;
  /** Pixel height of the dock bar (for drop zone detection) */
  dockHeight?: number;
  /** Callback fired during dock icon drag with cursor position; null on drag end */
  onDockDragMove?: (iconId: string, cursorX: number, cursorY: number) => void;
  /** Callback fired when dock icon drag ends */
  onDockDragEnd?: (iconId: string) => void;
  /** Callback fired when desktop icon drag state changes */
  onDraggingChange?: (iconId: string, isDragging: boolean) => void;
  /** Notification badge count. Renders iOS-style red badge when > 0. */
  badgeCount?: number;
}

/* ==========================================================================
   COMPONENT
   ========================================================================== */

/**
 * A draggable desktop icon component.
 *
 * @remarks
 * - Supports drag-and-drop repositioning
 * - Distinguishes between clicks and drags (5px threshold)
 * - Exposes position via ref for window minimize animations
 *
 * @example
 * ```tsx
 * <DesktopIcon
 *   ref={iconRef}
 *   config={{
 *     id: 'terminal-icon',
 *     windowId: 'terminal',
 *     icon: <Terminal className="w-8 h-8 text-green-400" />,
 *     label: 'Terminal',
 *   }}
 *   onClick={() => openWindow('terminal')}
 * />
 * ```
 */
export const DesktopIcon = forwardRef<DesktopIconRef, DesktopIconProps>(
  function DesktopIcon(
    {
      config,
      onClick,
      className = "",
      isDock = false,
      isOpen = false,
      isMobile = false,
      isMobileGrid = false,
      isJiggling = false,
      onDockReorder,
      dockIndex,
      onPositionChange,
      onContextMenu,
      onDragOffDock,
      onDragToDock,
      dockHeight = 80,
      onDockDragMove,
      onDockDragEnd,
      onDraggingChange,
      badgeCount = 0,
    },
    ref
  ) {
    const { icon, label, initialX = 0, initialY = 0 } = config;

    // Position state for drag-and-drop
    const [position, setPosition] = useState({ x: initialX, y: initialY });
    const [isDragging, setIsDragging] = useState(false);

    // Refs for tracking drag state
    const dragRef = useRef({
      startX: 0,
      startY: 0,
      hasMoved: false,
      mouseDownX: 0, // raw cursor position at mousedown
      mouseDownY: 0,
    });
    const iconRef = useRef<HTMLDivElement>(null);

    // Store callbacks in refs to avoid effect dependency churn
    const onClickRef = useRef(onClick);
    onClickRef.current = onClick;
    const onPositionChangeRef = useRef(onPositionChange);
    onPositionChangeRef.current = onPositionChange;
    const onDragToDockRef = useRef(onDragToDock);
    onDragToDockRef.current = onDragToDock;
    const onDragOffDockRef = useRef(onDragOffDock);
    onDragOffDockRef.current = onDragOffDock;
    const onDockReorderRef = useRef(onDockReorder);
    onDockReorderRef.current = onDockReorder;
    const onDockDragMoveRef = useRef(onDockDragMove);
    onDockDragMoveRef.current = onDockDragMove;
    const onDockDragEndRef = useRef(onDockDragEnd);
    onDockDragEndRef.current = onDockDragEnd;
    const onDraggingChangeRef = useRef(onDraggingChange);
    onDraggingChangeRef.current = onDraggingChange;

    /* ======================================================================
       REF IMPERATIVE HANDLE
       ====================================================================== */

    useImperativeHandle(ref, () => ({
      getPosition: () => {
        if (iconRef.current) {
          const rect = iconRef.current.getBoundingClientRect();
          return { x: rect.left, y: rect.top };
        }
        return { x: position.x, y: position.y };
      },
    }));

    /* ======================================================================
       DRAG HANDLERS
       ====================================================================== */

    // Dock reorder drag state
    const [dockDragging, setDockDragging] = useState(false);
    const [, setDockDragOffset] = useState({ x: 0, y: 0 });
    const dockDragRef = useRef({
      startX: 0,
      startY: 0,
      hasMoved: false,
      startIndex: 0,
      mouseDownX: 0,
      mouseDownY: 0,
    });
    const dockOriginRef = useRef<{ left: number; top: number } | null>(null);

    const handleRightClick = useCallback(
      (e: React.MouseEvent | React.PointerEvent) => {
        if (onContextMenu) {
          e.preventDefault();
          onContextMenu(config.id, e.clientX, e.clientY);
        }
      },
      [onContextMenu, config.id]
    );

    // Refs to hold active drag listener cleanup functions
    const desktopDragCleanupRef = useRef<(() => void) | null>(null);
    const dockDragCleanupRef = useRef<(() => void) | null>(null);

    // Clean up any lingering listeners on unmount
    useEffect(() => {
      return () => {
        desktopDragCleanupRef.current?.();
        dockDragCleanupRef.current?.();
      };
    }, []);

    const handlePointerDown = (e: React.PointerEvent) => {
      if (e.button === 2) return; // right-click handled separately

      // In dock mode with reorder support
      if (isDock && onDockReorder && dockIndex !== undefined) {
        e.preventDefault();
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        dockDragRef.current = {
          startX: e.clientX,
          startY: e.clientY,
          hasMoved: false,
          startIndex: dockIndex,
          mouseDownX: e.clientX,
          mouseDownY: e.clientY,
        };
        // Capture the icon's static position before any drag transforms
        if (iconRef.current) {
          const rect = iconRef.current.getBoundingClientRect();
          dockOriginRef.current = { left: rect.left, top: rect.top };
        }
        setDockDragging(true);
        startDockDragListeners();
        return;
      }

      // In dock mode without reorder, just handle click directly
      if (isDock) {
        return;
      }

      // Desktop mode drag — attach listeners synchronously
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);

      if (onPositionChange) {
        const rect = iconRef.current?.getBoundingClientRect();
        dragRef.current = {
          startX: rect ? e.clientX - (rect.left + rect.width / 2) : 0,
          startY: rect ? e.clientY - (rect.top + rect.height / 2) : 0,
          hasMoved: false,
          mouseDownX: e.clientX,
          mouseDownY: e.clientY,
        };
      } else {
        dragRef.current = {
          startX: e.clientX - position.x,
          startY: e.clientY - position.y,
          hasMoved: false,
          mouseDownX: e.clientX,
          mouseDownY: e.clientY,
        };
      }

      setIsDragging(true);
      onDraggingChangeRef.current?.(config.id, true);
      startDesktopDragListeners();
    };

    // Track whether a dock drag just completed to suppress the click event that follows pointerup
    const dockDragJustEndedRef = useRef(false);

    const handleClick = () => {
      // In dock mode, clicks are handled directly — but suppress if a drag just ended
      if (isDock) {
        if (dockDragJustEndedRef.current) {
          dockDragJustEndedRef.current = false;
          return;
        }
        onClick();
      }
    };

    const handleIntentToOpen = useCallback(() => {
      preloadFarmAssets(config.windowId);
    }, [config.windowId]);

    // Attach document listeners synchronously (not via useEffect)
    function startDesktopDragListeners() {
      // Remove any stale listeners first
      desktopDragCleanupRef.current?.();

      const handlePointerMove = (e: PointerEvent) => {
        if (!dragRef.current.hasMoved) {
          const dx = e.clientX - dragRef.current.mouseDownX;
          const dy = e.clientY - dragRef.current.mouseDownY;
          if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
            dragRef.current.hasMoved = true;
          }
        }

        if (onPositionChangeRef.current) {
          if (dragRef.current.hasMoved) {
            const cursorX = e.clientX - dragRef.current.startX;
            const cursorY = e.clientY - dragRef.current.startY;
            const xPct = (cursorX / window.innerWidth) * 100;
            const yPct = (cursorY / window.innerHeight) * 100;
            onPositionChangeRef.current(
              config.id,
              Math.max(2, Math.min(98, xPct)),
              Math.max(2, Math.min(98, yPct))
            );
          }
        } else {
          const newX = e.clientX - dragRef.current.startX;
          const newY = e.clientY - dragRef.current.startY;
          setPosition({ x: newX, y: newY });
        }
      };

      const handlePointerUp = (e: PointerEvent) => {
        cleanup();
        setIsDragging(false);
        onDraggingChangeRef.current?.(config.id, false);

        const totalDx = e.clientX - dragRef.current.mouseDownX;
        const totalDy = e.clientY - dragRef.current.mouseDownY;
        const didMove =
          dragRef.current.hasMoved ||
          Math.abs(totalDx) > 5 ||
          Math.abs(totalDy) > 5;

        if (!didMove) {
          onClickRef.current();
          return;
        }

        if (onPositionChangeRef.current && onDragToDockRef.current) {
          const droppedOnDock =
            e.clientY > window.innerHeight - dockHeight - 20;
          if (droppedOnDock) {
            onDragToDockRef.current(config.id, e.clientX);
            return;
          }
        }
      };

      function cleanup() {
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", handlePointerUp);
        desktopDragCleanupRef.current = null;
      }

      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
      desktopDragCleanupRef.current = cleanup;
    }

    // Attach dock drag listeners synchronously
    function startDockDragListeners() {
      dockDragCleanupRef.current?.();

      const handlePointerMove = (e: PointerEvent) => {
        const dx = e.clientX - dockDragRef.current.startX;
        const dy = e.clientY - dockDragRef.current.startY;

        if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
          dockDragRef.current.hasMoved = true;
        }

        if (dockDragRef.current.hasMoved && onDockDragMoveRef.current) {
          onDockDragMoveRef.current(config.id, e.clientX, e.clientY);
        }

        setDockDragOffset({ x: dx, y: dy });
      };

      const handlePointerUp = (e: PointerEvent) => {
        cleanup();
        setDockDragging(false);
        setDockDragOffset({ x: 0, y: 0 });
        dockOriginRef.current = null;
        onDockDragEndRef.current?.(config.id);

        const totalDx = e.clientX - dockDragRef.current.mouseDownX;
        const totalDy = e.clientY - dockDragRef.current.mouseDownY;
        const didMove =
          dockDragRef.current.hasMoved ||
          Math.abs(totalDx) > 5 ||
          Math.abs(totalDy) > 5;

        if (!didMove) {
          // No movement — treat as click. The pointerup handler calls onClick directly;
          // suppress the subsequent DOM click event so the window doesn't open twice.
          dockDragJustEndedRef.current = true;
          onClickRef.current();
          return;
        }

        // A drag happened — suppress the subsequent DOM click event
        dockDragJustEndedRef.current = true;

        // Check if dropped outside the dock area
        const droppedAboveDock =
          e.clientY < window.innerHeight - dockHeight - 40;
        if (droppedAboveDock && onDragOffDockRef.current) {
          const xPct = (e.clientX / window.innerWidth) * 100;
          const yPct = (e.clientY / window.innerHeight) * 100;
          onDragOffDockRef.current(
            config.id,
            Math.max(2, Math.min(98, xPct)),
            Math.max(2, Math.min(98, yPct))
          );
          return;
        }

        // Reorder within dock using cursor position
        if (onDockReorderRef.current) {
          onDockReorderRef.current(dockDragRef.current.startIndex, e.clientX);
        }
      };

      function cleanup() {
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", handlePointerUp);
        dockDragCleanupRef.current = null;
      }

      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
      dockDragCleanupRef.current = cleanup;
    }

    /* ======================================================================
       RENDER
       ====================================================================== */

    const isStringIcon = typeof icon === "string";
    const isElementIcon = isValidElement(icon);
    const showBadge = badgeCount > 0;
    const badgeLabel = badgeCount > 99 ? "99+" : String(badgeCount);

    // Mobile grid mode: iOS-style icon with label
    if (isMobileGrid) {
      // Random jiggle delay per icon (stable via id hash)
      const jiggleDelay = isJiggling
        ? `${(config.id.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 5) * 0.06}s`
        : undefined;

      return (
        <div
          ref={iconRef}
          data-icon-id={config.id}
          onMouseEnter={handleIntentToOpen}
          onPointerDown={handleIntentToOpen}
          onClick={isJiggling ? undefined : onClick}
          className={`flex w-[78px] select-none flex-col items-center ${isJiggling ? "" : "cursor-pointer active:scale-90"} ${isJiggling ? "animate-jiggle" : "transition-transform duration-100"} ${className} `}
          style={isJiggling ? { animationDelay: jiggleDelay } : undefined}
        >
          {/* Icon container — forces child icon components to fill */}
          <div className="relative">
            <div
              className={`h-[72px] w-[72px] overflow-hidden rounded-[18px] ${isStringIcon ? "flex items-center justify-center bg-white/10 text-5xl shadow-lg backdrop-blur-sm" : ""} ${isElementIcon ? "[&>div]:h-full [&>div]:w-full [&>div]:rounded-[16px] [&_svg]:h-11 [&_svg]:w-11" : ""} `}
            >
              {isStringIcon && icon}
              {isElementIcon && icon}
            </div>
            {/* Notification badge */}
            <div
              className={`pointer-events-none absolute -right-[7px] -top-[7px] z-10 flex min-w-[21px] items-center justify-center rounded-full bg-[#FF3B30] px-1 text-[12px] font-bold leading-[19px] text-white transition-all duration-200 ${showBadge ? "scale-100" : "scale-0"} `}
            >
              {badgeLabel}
            </div>
          </div>
          {/* Label */}
          <span className="mt-1.5 w-full truncate px-0.5 text-center text-[11px] font-medium leading-tight text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
            {label}
          </span>
        </div>
      );
    }

    // Dock mode: compact icon with hover effects, no label, drag-to-reorder
    if (isDock) {
      const isDraggingOffDock = dockDragging && dockDragRef.current.hasMoved;

      // Mobile dock: match the grid icon size (66px) so dock aligns with page rows
      if (isMobile) {
        return (
          <div
            ref={iconRef}
            data-icon-id={config.id}
            onMouseEnter={handleIntentToOpen}
            onClick={handleClick}
            onPointerDown={handlePointerDown}
            onContextMenu={handleRightClick}
            style={{
              ...(isDraggingOffDock ? { opacity: 0, transition: "none" } : {}),
              touchAction: "none",
              ...(isJiggling
                ? {
                    animationDelay: `${(config.id.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 5) * 0.06}s`,
                  }
                : {}),
            }}
            className={`flex w-[78px] cursor-pointer select-none flex-col items-center ${isJiggling ? "animate-jiggle" : "transition-transform duration-100"} ${isJiggling ? "" : "active:scale-90"} ${className} `}
          >
            <div className="relative">
              <div
                className={`pointer-events-none h-[72px] w-[72px] overflow-hidden rounded-[18px] ${isStringIcon ? "flex items-center justify-center bg-white/10 text-5xl shadow-lg backdrop-blur-sm" : ""} ${isElementIcon ? "[&>div]:h-full [&>div]:w-full [&>div]:rounded-[16px] [&_svg]:h-11 [&_svg]:w-11" : ""} `}
              >
                {isStringIcon && icon}
                {isElementIcon && icon}
              </div>
              {/* Notification badge */}
              <div
                className={`pointer-events-none absolute -right-[7px] -top-[7px] z-10 flex min-w-[21px] items-center justify-center rounded-full bg-[#FF3B30] px-1 text-[12px] font-bold leading-[19px] text-white transition-all duration-200 ${showBadge ? "scale-100" : "scale-0"} `}
              >
                {badgeLabel}
              </div>
            </div>
          </div>
        );
      }

      // Desktop dock
      const iconSizeClass = isStringIcon
        ? "text-[2.75rem]"
        : "w-14 h-14 flex items-center justify-center";

      return (
        <div
          ref={iconRef}
          data-icon-id={config.id}
          onMouseEnter={handleIntentToOpen}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onContextMenu={handleRightClick}
          style={
            isDraggingOffDock
              ? { opacity: 0, transition: "none", touchAction: "none" }
              : { touchAction: "none" }
          }
          className={`group flex cursor-pointer select-none flex-col items-center transition-all duration-200 ease-out ${dockDragging ? "" : "hover:-translate-y-2 hover:scale-125"} flex-shrink-0 ${className} `}
        >
          <div className="relative">
            <div className={`pointer-events-none ${iconSizeClass} `}>
              {isStringIcon && icon}
              {isElementIcon && icon}
            </div>
            {/* Notification badge */}
            <div
              className={`pointer-events-none absolute -right-[7px] -top-[7px] z-10 flex min-w-[23px] items-center justify-center rounded-full bg-[#FF3B30] px-1 text-[12px] font-bold leading-[21px] text-white transition-all duration-200 ${showBadge ? "scale-100" : "scale-0"} `}
            >
              {badgeLabel}
            </div>
          </div>
          {/* Open indicator dot */}
          <div
            className={`mt-1 h-1 w-1 rounded-full bg-white transition-opacity duration-200 ${isOpen ? "opacity-100" : "opacity-0"} `}
          />
        </div>
      );
    }

    // Traditional desktop icon mode
    // When externally positioned (onPositionChange), don't apply internal transform
    const desktopStyle: React.CSSProperties = onPositionChange
      ? { cursor: isDragging ? "grabbing" : "auto" }
      : {
          transform: `translate(${position.x}px, ${position.y}px)`,
          cursor: isDragging ? "grabbing" : "auto",
        };

    return (
      <div
        ref={iconRef}
        data-icon-id={config.id}
        onMouseEnter={handleIntentToOpen}
        onPointerDown={handlePointerDown}
        onContextMenu={handleRightClick}
        style={{ ...desktopStyle, touchAction: "none" }}
        className={`group flex w-28 select-none flex-col items-center gap-1.5 rounded-lg p-2 transition-colors hover:bg-white/10 ${className} `}
      >
        {/* Icon */}
        <div className="relative">
          <div
            className={`pointer-events-none ${isStringIcon ? "text-5xl" : "flex h-14 w-14 items-center justify-center"} `}
          >
            {isStringIcon && icon}
            {isElementIcon && icon}
          </div>
          {/* Notification badge */}
          <div
            className={`pointer-events-none absolute -right-[7px] -top-[7px] z-10 flex min-w-[23px] items-center justify-center rounded-full bg-[#FF3B30] px-1 text-[12px] font-bold leading-[21px] text-white transition-all duration-200 ${showBadge ? "scale-100" : "scale-0"} `}
          >
            {badgeLabel}
          </div>
        </div>

        {/* Label */}
        <span className="pointer-events-none text-center text-xs font-bold leading-tight text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
          {label}
        </span>
      </div>
    );
  }
);
